import { ComponentRef, Injectable } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { EsOcrQuickFixSidebarComponent } from '../components/es-ocr-quick-fix-sidebar/es-ocr-quick-fix-sidebar.component';
import { AppService } from '@app/app.service';
import { ISectionFormField } from '@interfaces/section-form-field';
import { EntrySummaryModel, InvoiceModel } from '@app/data-layer/models/entry-summaries';
import { ShipmentModel } from '@app/data-layer/models/common';
import { CUSTOM_MASK } from '@helpers/custom-validator';
import { enumEntrySummaryQuickFixStep } from '../enums';
import { cloneDeep, findLastIndex } from 'lodash-es';
import { enumEntrySummarySubStep } from '@app/modules/entry-summary/enums';
import { DocumentPreviewModel } from '@app/data-layer/models/documents';
import { QuickFixStepModel } from '../interfaces';
import { EntrySummaryValidationService } from '@services/entry-summary-validation.service';
import {
  COUNTRY_OF_EXPORT_MISSING,
  INVOICE_MANUFACTURER_MISSING,
  INVOICE_OCR_STATE_NOT_REVIEWED,
  LINES_MISSING,
  MANUFACTURER_EXPECTED_TO_BE_CANADIAN,
} from '@constants/entity-validation-messages';
import { INPUT_TYPES_MAP, InputTypeMap } from '@constants/input-types-map';
import { UtilsHelper } from '@helpers/utils-helper';
import { CompanyBondsRepository } from '@app/data-layer/repositories';
import { CompaniesService } from '@services/companies.service';
import { take } from 'rxjs/operators';
import { CompanyBondModel } from '@app/data-layer/models/companies';
import { enumBondType } from '@app/data-layer/enums/common';
import { enumUserBetaFlag } from '@app/data-layer/enums/user';
import { EntrySummaryActionsHelper } from '@helpers/entry-summary-actions.helper';
import { HtsLineStatusHelper } from '@shared/hts-assistant/helpers/hts-line-status.helper';

interface StepStateModel {
  previous: QuickFixStepModel;
  current: QuickFixStepModel;
}

interface StepsStateModel {
  previous: QuickFixStepModel[];
  current: QuickFixStepModel[];
}

interface PreviewModalDocsState {
  previous: DocumentPreviewModel[];
  current: DocumentPreviewModel[];
}

@Injectable({ providedIn: 'root' })
export class EsOcrQuickFixService {
  public isOpen$: ReplaySubject<boolean> = new ReplaySubject();
  public stepChanged$: ReplaySubject<StepStateModel> = new ReplaySubject();
  public stepsChanged$: ReplaySubject<StepsStateModel> = new ReplaySubject();
  public previewModalDocs$: ReplaySubject<PreviewModalDocsState> = new ReplaySubject();

  private _entrySummary: EntrySummaryModel;
  private _steps: QuickFixStepModel[] = [];
  private _currencyInputMap: InputTypeMap = INPUT_TYPES_MAP.CURRENCY;

  private _step: QuickFixStepModel = {
    shipmentId: null,
    invoiceId: null,
    lineItemId: null,
    manufacturerId: null,
    type: enumEntrySummaryQuickFixStep.MAIN_SHIPMENT_DETAILS,
  };

  private _previewModalDocs: DocumentPreviewModel[] = [];
  private EEZY_BOND_CODE: string = '856';

  private sidebarComponentRef: ComponentRef<EsOcrQuickFixSidebarComponent>;

  private constructor(
    private appService: AppService,
    private companiesService: CompaniesService,
    private companyBondsRepository: CompanyBondsRepository,
    private entrySummaryValidationService: EntrySummaryValidationService,
  ) {}

  public openSidebar(entrySummary: EntrySummaryModel): ComponentRef<any> {
    this.entrySummary = entrySummary;
    this.closeSidebar();

    this.isOpen$.next(true);

    this.sidebarComponentRef = this.appService.openSidebar(EsOcrQuickFixSidebarComponent, {
      sidebarHeaderText: 'BUTTON.QUICK_FIX',
      isMd: true,
      isSidebarHeaderTitleCase: true,
      customZIndex: '1031',
      hideBackDrop: true,
      isEnableScrolling: true,
    });

    this.sidebarComponentRef.instance.entrySummary = this.entrySummary;

    return this.sidebarComponentRef;
  }

  public closeSidebar(): void {
    this.sidebarComponentRef?.destroy();
    this.appService.closeSidebar();
    this.isOpen$.next(false);
    this.setCurrentStep(null);
  }

  public getSidebarSectionFields(stepType: enumEntrySummarySubStep): ISectionFormField[] {
    let fields: ISectionFormField[] = [];
    switch (stepType) {
      //<-- Shipment Details -->
      case enumEntrySummarySubStep.SHIPMENT_DETAILS:
        fields = [
          {
            type: 'text',
            name: 'vesselName',
            label: 'LABELS.VESSEL_NAME',
            patterns: CUSTOM_MASK,
            mask: 'F{20}',
            maxMaskLength: 20,
            maskErrorTooltip: 'MASK_ERROR.ONLY_ENGLISH_DIGITS_AND_SPECIAL',
            dropSpecialCharacters: false,
          },
          {
            type: 'text',
            name: 'trip',
            label: '', //dynamically set in component
            patterns: CUSTOM_MASK,
            mask: 'F{5}',
            maxMaskLength: 5,
            maskErrorTooltip: 'MASK_ERROR.ONLY_ENGLISH_DIGITS_AND_SPECIAL',
            dropSpecialCharacters: false,
          },
        ];
        break;
      case enumEntrySummarySubStep.FROM:
        fields = [
          {
            id: 'foreignPortOfLadingId',
            type: 'select',
            name: 'foreignPortOfLadingId',
            label: 'FORM_LABEL.FOREIGN_PORT',
            bindValue: 'id',
            bindLabel: ['code', 'portName', 'countryCode'],
            bindOption: ['code', 'portName', 'countryCode'],
            placeholder: 'FORM_PLACEHOLDER.SEARCH_CODE_OR_NAME',
            helper: 'FORM_HELPER.FOREIGN_PORT_OF_LADING',
            isShowHelper: true,
          },
          {
            id: 'exportDate',
            type: 'date',
            name: 'exportDate',
            label: 'FORM_LABEL.EXPORT_DATE',
            placeholder: 'FORM_PLACEHOLDER.CHOOSE_DATE',
            showCurrentDate: true,
          },
        ];
        break;
      case enumEntrySummarySubStep.TO:
        fields = [
          {
            id: 'locationCode',
            type: 'select',
            name: 'locationCode',
            label: 'FORM_LABEL.LOCATION_OF_GOODS',
            bindValue: 'code',
            bindLabel: ['code', 'name'],
            helper: 'FORM_HELPER.LOCATION_OF_GOODS',
            isShowHelper: true,
            updateCountCharacter: 3,
          },
          {
            id: 'portOfEntryCode',
            type: 'select',
            name: 'portOfEntryCode',
            label: 'FORM_LABEL.PORT_OF_ENTRY',
            bindValue: 'code',
            bindLabel: ['code', 'description'],
            helper: 'FORM_HELPER.PORT_OF_ENTRY',
            isShowHelper: true,
            placeholder: 'FORM_PLACEHOLDER.SEARCH_CODE_OR_NAME',
            updateCountCharacter: 0,
            dropdownPosition: 'top',
            searchOnOpen: true,
          },
          {
            id: 'stateOfDestination',
            type: 'select',
            name: 'stateOfDestination',
            label: 'FORM_LABEL.STATE_OF_DESTINATION',
            bindValue: 'code',
            bindLabel: ['code', 'description'],
            bindOption: ['code', 'description'],
            dropdownPosition: 'top',
          },
          {
            id: 'arrivalDate',
            type: 'date',
            name: 'arrivalDate',
            label: 'LABELS.ARRIVAL_DATE',
            placeholder: 'FORM_PLACEHOLDER.CHOOSE_DATE',
            showCurrentDate: true,
          },
        ];
        break;
      case enumEntrySummarySubStep.VIA:
        fields = [
          {
            id: 'differentPortEntry',
            type: 'checkbox',
            name: 'differentPortEntry',
            label: 'ENTRY_SUMMARY.VIA_DESC',
          },
          {
            id: 'portOfUnladingCode',
            type: 'select',
            name: 'portOfUnladingCode',
            label: 'FORM_LABEL.PORT_OF_UNLADING_ARRIVAL',
            helper: 'FORM_HELPER.PORT_OF_UNLADING_ARRIVAL',
            isShowHelper: true,
            infoTooltip: 'LABELS.EDIT_SHIPMENT_DETAILS_DESCRIPTION',
            placeholder: 'FORM_PLACEHOLDER.SEARCH_CODE_OR_NAME',
            bindLabel: ['code', 'description'],
            bindValue: 'code',
          },
        ];
        break;
      //<-- Bl Details -->
      case enumEntrySummarySubStep.MASTER_SCAC:
        fields = [
          {
            type: 'text',
            name: 'masterBillCarrier',
            fgName: ['masterBill', 'carrier'],
            dropSpecialCharacters: false,
            isShowHelper: true,
            //Fields below are dynamically set based on bl enumTransportationMode
            helper: '',
            label: '',
            patterns: null,
            mask: '',
            maxMaskLength: null,
            maskErrorTooltip: '',
          },
        ];
        break;
      case enumEntrySummarySubStep.MASTER_BL:
        fields = [
          {
            type: 'text',
            name: 'masterBillNumber',
            fgName: ['masterBill', 'number'],
            dropSpecialCharacters: false,
            isShowHelper: true,
            //Fields below are dynamically set in component based on bl enumTransportationMode
            helper: '',
            label: '',
            patterns: null,
            mask: '',
            maxMaskLength: null,
            maskErrorTooltip: '',
          },
        ];
        break;
      case enumEntrySummarySubStep.HOUSE_SCAC:
        fields = [
          {
            type: 'text',
            name: 'houseBillCarrier',
            fgName: ['houseBill', 'carrier'],
            dropSpecialCharacters: false,
            isShowHelper: true,
            //Fields below are dynamically set in component based on bl enumTransportationMode
            label: '',
            patterns: null,
            mask: '',
            maxMaskLength: null,
            maskErrorTooltip: '',
            validation: true,
          },
        ];
        break;
      case enumEntrySummarySubStep.HOUSE_BL:
        fields = [
          {
            type: 'text',
            name: 'houseBillNumber',
            fgName: ['houseBill', 'number'],
            dropSpecialCharacters: false,
            isShowHelper: true,
            //Fields below are dynamically set in component based on bl enumTransportationMode
            label: '',
            patterns: null,
            mask: '',
            maxMaskLength: null,
            maskErrorTooltip: '',
          },
        ];
        break;
      case enumEntrySummarySubStep.QUANTITY:
        fields = [
          {
            type: 'text',
            name: 'quantity',
            label: 'FORM_LABEL.TOTAL_QUANTITY',
            dropSpecialCharacters: false,
            inputMode: 'number',
            //Fields below are dynamically set in component based on bl enumTransportationMode
            patterns: null,
            mask: null,
            maskErrorTooltip: null,
          },
          {
            id: 'quantityUnit',
            type: 'select',
            name: 'quantityUnit',
            label: 'FORM_LABEL.UNIT_OF_MEASURE',
            bindValue: 'code',
            bindLabel: 'code',
            dropdownPosition: 'top',
            isNgSelect: true,
          },
        ];
        break;
      case enumEntrySummarySubStep.IT:
        fields = [
          {
            type: 'text',
            name: 'itNumber',
            label: 'FORM_LABEL.IT_NUMBER',
            helper: 'FORM_HELPER.IT_NUMBER',
            isShowHelper: true,
            mask: 'A{11}',
            maxMaskLength: 11,
            maskErrorTooltip: 'MASK_ERROR.IT_NUMBER',
            inputMode: 'text',
            placeholder: 'FORM_PLACEHOLDER.ENTER',
          },
          {
            id: 'sidebar-itDate',
            type: 'date',
            name: 'itDate',
            label: 'FORM_LABEL.IT_DATE',
            helper: 'FORM_HELPER.IT_DATE',
            placeholder: 'FORM_PLACEHOLDER.CHOOSE',
          },
        ];
        break;
      //<-- Seller + Consignee Details -->
      case enumEntrySummarySubStep.SELLER_DETAILS:
        fields = [
          {
            type: 'text',
            name: 'name',
            dropSpecialCharacters: false,
            patterns: CUSTOM_MASK,
            mask: 'F*',
            maskErrorTooltip: 'MASK_ERROR.ONLY_ENGLISH_DIGITS_AND_SPECIAL',
            label: 'LABELS.NAME',
            infoTooltip: 'INFO.SE.SUB_TITLE_DESKTOP',
          },
        ];
        break;
      case enumEntrySummarySubStep.CONSIGNEE_DETAILS:
        fields = [
          {
            type: 'text',
            name: 'name',
            dropSpecialCharacters: false,
            patterns: CUSTOM_MASK,
            mask: 'F*',
            maskErrorTooltip: 'MASK_ERROR.ONLY_ENGLISH_DIGITS_AND_SPECIAL',
            label: 'LABELS.NAME',
            infoTooltip: 'ENTITY.CONSIGNEE',
          },
        ];
        break;
      case enumEntrySummarySubStep.BUYER_DETAILS:
        fields = [
          {
            type: 'text',
            name: 'name',
            dropSpecialCharacters: false,
            patterns: CUSTOM_MASK,
            mask: 'F*',
            maskErrorTooltip: 'MASK_ERROR.ONLY_ENGLISH_DIGITS_AND_SPECIAL',
            label: 'LABELS.NAME',
            infoTooltip: 'ENTITY.BUYER',
          },
        ];
        break;
      //<-- Shipping Terms of Sale -->
      case enumEntrySummarySubStep.SHIPPING_TERMS_OF_SALE:
        fields = [
          {
            type: 'text',
            name: 'chargesDistributionType',
            label: 'FORM_LABEL.CHARGES_DISTRIBUTION_TYPE_DESKTOP',
            isHidden: true,
          },
          {
            type: 'text',
            inputMode: 'numeric',
            name: 'freightCost',
            label: 'FORM_LABEL.FREIGHT_COST',
            isShowTaggedBox: true,
            helper: 'FORM_HELPER.AT_LEAST_ONE_PER_HTS_CODE',
            isShowHelper: true,
            pattern: this._currencyInputMap.pattern,
            mask: this._currencyInputMap.mask,
            maskErrorTooltip: this._currencyInputMap.maskErrorTooltip,
            thousandSeparator: this._currencyInputMap.thousandSeparator,
          },
          {
            type: 'text',
            inputMode: 'numeric',
            name: 'insurance',
            label: 'FORM_LABEL.INSURANCE',
            isShowTaggedBox: true,
            pattern: this._currencyInputMap.pattern,
            mask: this._currencyInputMap.mask,
            maskErrorTooltip: this._currencyInputMap.maskErrorTooltip,
            thousandSeparator: this._currencyInputMap.thousandSeparator,
          },
          {
            type: 'text',
            inputMode: 'numeric',
            name: 'transportationCharge',
            label: 'FORM_LABEL.TRANSPORTATION',
            isShowTaggedBox: true,
            pattern: this._currencyInputMap.pattern,
            mask: this._currencyInputMap.mask,
            maskErrorTooltip: this._currencyInputMap.maskErrorTooltip,
            thousandSeparator: this._currencyInputMap.thousandSeparator,
          },
        ];
        break;
      //<-- Invoice Main Details -->
      case enumEntrySummarySubStep.INVOICE_MAIN_DETAILS:
        fields = [
          {
            type: 'text',
            name: 'invoiceNumber',
            label: 'LABELS.INVOICE_NUMBER',
            mask: 'F{17}',
            maskErrorTooltip: 'MASK_ERROR.ONLY_ENGLISH_DIGITS_AND_SPECIAL',
            maxMaskLength: 17,
            dropSpecialCharacters: false,
            patterns: CUSTOM_MASK,
          },
          {
            type: 'select',
            name: 'currency',
            label: 'LABELS.CURRENCY',
            bindLabel: 'code',
            bindValue: 'code',
            comparisonLabel: 'LABELS.RATE',
            clearable: false,
            //Fields below are dynamically set in component
            comparisonValue: null,
            items: [],
          },
          {
            type: 'select',
            name: 'incoTermsCode',
            bindValue: 'code',
            placeholder: 'FORM_PLACEHOLDER.SELECT',
            labelTemplateFieldNames: ['code', 'name'],
            optionTemplateFieldNames: ['code', 'name'],
            //Fields below are dynamically set in component
            label: '',
            searchFn: null,
            items: [],
          },
          {
            type: 'select',
            name: 'countryOfExportId',
            label: 'LABELS.COUNTRY_OF_EXPORT',
            bindLabel: ['code', 'description'],
            bindValue: 'id',
            placeholder: 'FORM_PLACEHOLDER.SELECT',
            helper: 'FORM_HELPER.COUNTRY_OF_EXPORT',
            isShowHelper: true,
            //Fields below are dynamically set in component
            initializedItems: [],
            change: null,
          },
        ];
        break;
      case enumEntrySummarySubStep.MANUFACTURER_DETAILS:
        fields = [
          {
            type: 'text',
            name: 'name',
            dropSpecialCharacters: false,
            patterns: CUSTOM_MASK,
            mask: 'F*',
            maskErrorTooltip: 'MASK_ERROR.ONLY_ENGLISH_DIGITS_AND_SPECIAL',
            label: 'LABELS.NAME',
            placeholder: 'LABELS.COMPANY_NAME',
            helper: 'FORM_LABEL.AS_IN_COMMERCIAL_INVOICE',
            isShowHelper: true,
          },
        ];
        break;
      case enumEntrySummarySubStep.LINE_ITEM:
        //Handled by seperate component
        break;
      default:
        break;
    }
    return fields;
  }

  public getCurrentStep(): QuickFixStepModel {
    return this.step;
  }

  public setCurrentStep(step: QuickFixStepModel) {
    this.step = step;
  }

  public getCurrentStepsToFix(): QuickFixStepModel[] {
    return this.steps;
  }

  /**
   * #12863 — steps as the sidebar walks them: the fix steps plus one permanent "New line item" step per invoice.
   * The invoice-without-lines placeholder (LINE_ITEM with no lineItemId) becomes that invoice's add step in place;
   * every other invoice gets its add step right after its last fix step (or at the end when it has none).
   * Pure over `steps` — the header keeps counting only real fix steps.
   */
  public getSidebarSteps(): QuickFixStepModel[] {
    const sidebarSteps: QuickFixStepModel[] = this.steps.map((step: QuickFixStepModel) => {
      const isBlankLineItemStep: boolean = step.type === enumEntrySummaryQuickFixStep.LINE_ITEM && step.lineItemId === null;
      return isBlankLineItemStep ? { ...step, type: enumEntrySummaryQuickFixStep.NEW_LINE_ITEM } : { ...step };
    });

    (this.entrySummary?.shipments || []).forEach((shipment: ShipmentModel) => {
      (shipment.invoices || []).forEach((invoice: InvoiceModel) => {
        const isHasAddStep: boolean = sidebarSteps.some(
          (step: QuickFixStepModel) => step.type === enumEntrySummaryQuickFixStep.NEW_LINE_ITEM && step.invoiceId === invoice.id,
        );
        if (isHasAddStep) {
          return;
        }

        const addStep: QuickFixStepModel = {
          shipmentId: shipment.id,
          invoiceId: invoice.id,
          lineItemId: null,
          type: enumEntrySummaryQuickFixStep.NEW_LINE_ITEM,
        };
        const lastInvoiceStepIndex: number = findLastIndex(sidebarSteps, (step: QuickFixStepModel) => step.invoiceId === invoice.id);

        if (lastInvoiceStepIndex === -1) {
          sidebarSteps.push(addStep);
        } else {
          sidebarSteps.splice(lastInvoiceStepIndex + 1, 0, addStep);
        }
      });
    });

    return sidebarSteps;
  }

  public setCurrentStepsToFix(entrySummary: EntrySummaryModel) {
    this.entrySummary = entrySummary;
    const shipments = this.entrySummary.shipments;

    const newSteps: QuickFixStepModel[] = [];
    // #13403: for a tagged company a line the classification assistant has not verified yet is a step of its own
    // (an entry that cannot be edited any more gets no such steps: nothing could be done there)
    const isHtsAssistant: boolean =
      this.companiesService.hasUserBetaFlag(enumUserBetaFlag.htsClassificationAssistant) &&
      EntrySummaryActionsHelper.isCanEdit(entrySummary);

    this.companyBondsRepository
      .get(this.companiesService.getCompany().id, false, false)
      .pipe(take(1))
      .subscribe((bonds) => {
        const continuousBonds = (bonds || []).filter(
          (bond: CompanyBondModel) => bond.type === enumBondType.CONTINUOUS && (this.isOurBond(bond.suretyCode) ? true : bond.active),
        );

        shipments.forEach((shipment) => {
          const invoices = shipment.invoices;
          const isQuickFixShipmentMainDetailsInvalid = this.entrySummaryValidationService.isQuickFixShipmentMainDetailsInvalid(
            this.entrySummary,
            shipment,
          );

          const isQuickFixShipmentTypeDetailsInvalid = this.entrySummaryValidationService.isQuickFixShipmentTypeDetailsInvalid(
            this.entrySummary,
            continuousBonds,
          );

          const isQuickFixBlDetailsInvalid = this.entrySummaryValidationService.isQuickFixBlDetailsInvalid(entrySummary, shipment);

          const isQuickFixSellerAndConsigneeDetailsInvalid =
            this.entrySummaryValidationService.isQuickFixSellerAndConsigneeDetailsInvalid(shipment);

          const sellerAndConsigneeDetailsErrors = [
            ...(shipment.seller?.validationMessages || []),
            ...(shipment.consignee?.validationMessages || []),
            ...(shipment.buyer?.validationMessages || []),
          ];

          const isQuickFixTosDetailsInvalid = this.entrySummaryValidationService.isQuickFixTosDetailsInvalid(shipment);
          const isStepsAlreadyWithShipmentMainDetails = newSteps.some(
            (step) => step.type === enumEntrySummaryQuickFixStep.MAIN_SHIPMENT_DETAILS,
          );
          if (isQuickFixShipmentMainDetailsInvalid && !isStepsAlreadyWithShipmentMainDetails) {
            newSteps.push({
              shipmentId: shipment.id,
              type: enumEntrySummaryQuickFixStep.MAIN_SHIPMENT_DETAILS,
            });
          }

          if (isQuickFixShipmentTypeDetailsInvalid) {
            newSteps.push({
              shipmentId: shipment.id,
              type: enumEntrySummaryQuickFixStep.SHIPMENT_TYPE_DETAILS,
            });
          }

          if (isQuickFixBlDetailsInvalid) {
            newSteps.push({
              shipmentId: shipment.id,
              type: enumEntrySummaryQuickFixStep.BILL_OF_LADING_DETAILS,
            });
          }

          if (sellerAndConsigneeDetailsErrors.length || isQuickFixSellerAndConsigneeDetailsInvalid) {
            newSteps.push({
              shipmentId: shipment.id,
              type: enumEntrySummaryQuickFixStep.SELLER_AND_CONSIGNEE_DETAILS,
            });
          }

          if (isQuickFixTosDetailsInvalid) {
            newSteps.push({
              shipmentId: shipment.id,
              type: enumEntrySummaryQuickFixStep.SHIPPING_TERMS_OF_SALE,
            });
          }

          invoices.forEach((invoice) => {
            const invoiceErrorsToSkip = [
              INVOICE_OCR_STATE_NOT_REVIEWED,
              INVOICE_MANUFACTURER_MISSING,
              LINES_MISSING,
              MANUFACTURER_EXPECTED_TO_BE_CANADIAN,
            ];

            const invoiceMainDetailsErrors = (invoice?.validationMessages || []).filter((error) => {
              return !invoiceErrorsToSkip.includes(error.text);
            });
            const isQuickFixInvoiceDetailsInvalid = this.entrySummaryValidationService.isQuickFixInvoiceDetailsInvalid(invoice);

            if (invoiceMainDetailsErrors.length || isQuickFixInvoiceDetailsInvalid) {
              newSteps.push({
                shipmentId: shipment.id,
                invoiceId: invoice.id,
                type: enumEntrySummaryQuickFixStep.INVOICE_MAIN_DETAILS,
              });
            }

            const manufacturerDetailsErrors = invoice?.manufacturer?.validationMessages || [];
            const manufacturerAddressErrors = invoice?.manufacturer?.address?.validationMessages || {};
            const isQuickFixManufacturerDetailsInvalid = this.entrySummaryValidationService.isQuickFixManufacturerDetailsInvalid(invoice);
            const isManufacturerExpectedFromCanada =
              (invoice?.validationMessages || []).some((e) => e.text === MANUFACTURER_EXPECTED_TO_BE_CANADIAN) ||
              (invoice?.manufacturer?.validationMessages || []).some((e) => e.text === MANUFACTURER_EXPECTED_TO_BE_CANADIAN);

            if (
              manufacturerDetailsErrors.length ||
              !UtilsHelper.isObjectEmpty(manufacturerAddressErrors) ||
              isQuickFixManufacturerDetailsInvalid ||
              isManufacturerExpectedFromCanada
            ) {
              newSteps.push({
                shipmentId: shipment.id,
                invoiceId: invoice.id,
                manufacturerId: invoice?.manufacturer?.id,
                type: enumEntrySummaryQuickFixStep.MANUFACTURER,
              });
            }

            if (!invoice?.lines?.length) {
              newSteps.push({
                shipmentId: shipment.id,
                invoiceId: invoice.id,
                lineItemId: null,
                type: enumEntrySummaryQuickFixStep.LINE_ITEM,
              });
              return;
            }

            invoice.lines.forEach((lineItem) => {
              const lineItemErrorsToSkip = [COUNTRY_OF_EXPORT_MISSING];

              const lineItemDetailsErrors = (lineItem?.validationMessages || []).filter(
                (error) => !lineItemErrorsToSkip.includes(error.text),
              );
              const isNeedsHtsValidation: boolean = isHtsAssistant && HtsLineStatusHelper.isNeedsClassification(lineItem);

              if (lineItemDetailsErrors.length || isNeedsHtsValidation) {
                newSteps.push({
                  shipmentId: shipment.id,
                  invoiceId: invoice.id,
                  lineItemId: lineItem.id,
                  type: enumEntrySummaryQuickFixStep.LINE_ITEM,
                });
              }
            });
          });
        });
      });

    this.steps = newSteps;
  }

  public getEntrySummary(): EntrySummaryModel {
    return this.entrySummary;
  }

  public setEntrySummary(entrySummary: EntrySummaryModel) {
    this.entrySummary = entrySummary;
  }

  public getCurrentPreviewModalDocs(): DocumentPreviewModel[] {
    return this.previewModalDocs;
  }

  public setCurrentPreviewModalDocs(previewModalDocs: DocumentPreviewModel[]) {
    this.previewModalDocs = previewModalDocs;
  }

  private isOurBond(suretyCode: string): boolean {
    return suretyCode === this.EEZY_BOND_CODE;
  }

  private get entrySummary(): EntrySummaryModel {
    return this._entrySummary;
  }

  private set entrySummary(newEs: EntrySummaryModel) {
    this._entrySummary = newEs;
  }

  private get step(): QuickFixStepModel {
    return this._step;
  }

  private set step(newStep: QuickFixStepModel) {
    const previousStep = cloneDeep(this.step);
    this._step = newStep;

    const stepState: StepStateModel = {
      previous: previousStep,
      current: newStep,
    };

    this.stepChanged$.next(stepState);
  }

  private get steps(): QuickFixStepModel[] {
    return this._steps;
  }

  private set steps(newSteps: QuickFixStepModel[]) {
    const previousStep = cloneDeep(this.steps);
    this._steps = newSteps;

    const stepsState: StepsStateModel = {
      previous: previousStep,
      current: newSteps,
    };

    this.stepsChanged$.next(stepsState);
  }

  private get previewModalDocs(): DocumentPreviewModel[] {
    return this._previewModalDocs;
  }

  private set previewModalDocs(newPreviewModalDocs: DocumentPreviewModel[]) {
    const previousPreviewModalDocs = cloneDeep(this.previewModalDocs);
    this._previewModalDocs = newPreviewModalDocs;

    const previewModalDocsState: PreviewModalDocsState = {
      previous: previousPreviewModalDocs,
      current: newPreviewModalDocs,
    };

    this.previewModalDocs$.next(previewModalDocsState);
  }
}
